import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Save, AlertCircle, CheckCircle, Layout } from 'lucide-react';

interface AnalysisStyles {
  id: string;
  container_bg_color: string;
  container_border_color: string;
  container_border_width: number;
  container_border_radius: number;
  container_padding: number;
  text_color: string;
  heading_color: string;
  font_family: string;
  font_size: number;
  heading_size: number;
  line_height: number;
  line_color: string;
  line_opacity: number;
  line_width: number;
}

const fontFamilies = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Courier New, monospace', label: 'Courier New' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: 'Open Sans, sans-serif', label: 'Open Sans' },
  { value: 'Lato, sans-serif', label: 'Lato' }
];

const AnalysisStyles: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [styles, setStyles] = useState<AnalysisStyles | null>(null);

  useEffect(() => {
    fetchStyles();
  }, []);

  const fetchStyles = async () => {
    try {
      const { data, error } = await supabase.rpc('get_active_styles');

      if (error) throw error;
      setStyles(data);
      
      if (data) {
        applyStyles(data);
      }
    } catch (err) {
      console.error('Error fetching styles:', err);
      setError('Error al cargar los estilos');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!styles) return;

    const value = e.target.type === 'number' 
      ? parseFloat(e.target.value)
      : e.target.value;

    setStyles({
      ...styles,
      [e.target.name]: value
    });

    applyStyles({
      ...styles,
      [e.target.name]: value
    });
  };

  const applyStyles = (newStyles: AnalysisStyles) => {
    const style = document.createElement('style');
    style.textContent = `
      .analysis-container {
        background-color: ${newStyles.container_bg_color};
        border: ${newStyles.container_border_width}px solid ${newStyles.container_border_color};
        border-radius: ${newStyles.container_border_radius}px;
        padding: ${newStyles.container_padding}px;
        font-family: ${newStyles.font_family};
      }
      
      .analysis-container .prose {
        color: ${newStyles.text_color};
        font-size: ${newStyles.font_size}px;
        line-height: ${newStyles.line_height};
      }
      
      .analysis-container h1,
      .analysis-container h2,
      .analysis-container h3,
      .analysis-container h4,
      .analysis-container h5,
      .analysis-container h6 {
        color: ${newStyles.heading_color};
        font-size: ${newStyles.heading_size}px;
      }

      .analysis-container hr {
        border-color: ${newStyles.line_color};
        opacity: ${newStyles.line_opacity};
        border-width: ${newStyles.line_width}px;
      }
    `;

    const existingStyle = document.getElementById('analysis-styles');
    if (existingStyle) {
      existingStyle.replaceWith(style);
    } else {
      style.id = 'analysis-styles';
      document.head.appendChild(style);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!styles) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const { data, error } = await supabase.rpc('update_styles', {
        new_styles: styles
      });

      if (error) throw error;

      setSuccess('Estilos actualizados correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving styles:', err);
      setError('Error al guardar los estilos');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setLoading(true);
      setError('');
      await fetchStyles();
      setSuccess('Estilos restaurados correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error resetting styles:', err);
      setError('Error al restaurar los estilos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="card space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-white flex items-center">
            <Layout className="h-5 w-5 text-primary mr-2" />
            Estilos de Análisis
          </h3>
          <p className="text-sm text-gray-400">
            Personaliza la apariencia de los contenedores de análisis
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-900/20 border border-green-900/50 rounded-lg p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
          <p className="text-green-500">{success}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contenedor */}
          <div className="space-y-4">
            <h4 className="text-white font-medium">Contenedor</h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-300">
                Color de Fondo
              </label>
              <input
                type="color"
                name="container_bg_color"
                value={styles?.container_bg_color || '#1A1E25'}
                onChange={handleChange}
                className="mt-1 w-full h-10 rounded cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Color del Borde
              </label>
              <input
                type="color"
                name="container_border_color"
                value={styles?.container_border_color || '#2D3748'}
                onChange={handleChange}
                className="mt-1 w-full h-10 rounded cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Grosor del Borde (px)
              </label>
              <input
                type="number"
                name="container_border_width"
                value={styles?.container_border_width || 1}
                onChange={handleChange}
                min="0"
                max="10"
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Radio del Borde (px)
              </label>
              <input
                type="number"
                name="container_border_radius"
                value={styles?.container_border_radius || 8}
                onChange={handleChange}
                min="0"
                max="50"
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Padding (px)
              </label>
              <input
                type="number"
                name="container_padding"
                value={styles?.container_padding || 16}
                onChange={handleChange}
                min="0"
                max="50"
                className="input-field mt-1"
              />
            </div>
          </div>

          {/* Texto y Líneas */}
          <div className="space-y-4">
            <h4 className="text-white font-medium">Texto y Líneas</h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-300">
                Fuente
              </label>
              <select
                name="font_family"
                value={styles?.font_family || 'Arial, sans-serif'}
                onChange={handleChange}
                className="input-field mt-1"
              >
                {fontFamilies.map(font => (
                  <option key={font.value} value={font.value}>
                    {font.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Color del Texto
              </label>
              <input
                type="color"
                name="text_color"
                value={styles?.text_color || '#E2E8F0'}
                onChange={handleChange}
                className="mt-1 w-full h-10 rounded cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Color de Encabezados
              </label>
              <input
                type="color"
                name="heading_color"
                value={styles?.heading_color || '#FFFFFF'}
                onChange={handleChange}
                className="mt-1 w-full h-10 rounded cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Tamaño de Texto (px)
              </label>
              <input
                type="number"
                name="font_size"
                value={styles?.font_size || 16}
                onChange={handleChange}
                min="12"
                max="24"
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Tamaño de Encabezados (px)
              </label>
              <input
                type="number"
                name="heading_size"
                value={styles?.heading_size || 24}
                onChange={handleChange}
                min="16"
                max="36"
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Color de Líneas
              </label>
              <input
                type="color"
                name="line_color"
                value={styles?.line_color || '#2D3748'}
                onChange={handleChange}
                className="mt-1 w-full h-10 rounded cursor-pointer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Opacidad de Líneas
              </label>
              <input
                type="number"
                name="line_opacity"
                value={styles?.line_opacity || 0.2}
                onChange={handleChange}
                min="0"
                max="1"
                step="0.1"
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Grosor de Líneas (px)
              </label>
              <input
                type="number"
                name="line_width"
                value={styles?.line_width || 1}
                onChange={handleChange}
                min="1"
                max="5"
                className="input-field mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Altura de Línea
              </label>
              <input
                type="number"
                name="line_height"
                value={styles?.line_height || 1.5}
                onChange={handleChange}
                min="1"
                max="2"
                step="0.1"
                className="input-field mt-1"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleReset}
            className="btn-secondary"
          >
            Restaurar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center space-x-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                <span>Guardar Cambios</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AnalysisStyles;